import {
  ArrowDownTrayIcon,
  CheckIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { Fragment, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  deleteAutomateLoanDocsRecord,
  downloadS3Document,
  generateAutomateLoanDocs,
  getAutomateLoanDocsPackages,
  getAutomateLoanDocsRecords,
} from 'services'
import { Button } from 'stories/components'
import { confirm, formatTime } from 'utils'
import { RenderInput } from 'utils/RenderInput'

export default function AutomateLoanDocsPage({ hasOverview = true }: { hasOverview?: boolean }) {
  const [loading, setLoading] = useState(false)
  const [records, setRecords] = useState<Array<Record<string, any>>>([])
  const [packages, setPackages] = useState<Array<Record<string, any>>>([])
  const [packageId, setPackageId] = useState('')
  const [packagesError, setPackagesError] = useState('')
  const [tableData, setTableData] = useState<Record<string, any>>({})
  const [overrideValues, setOverrideValues] = useState<Record<string, string>>({})
  const [editingItems, setEditingItems] = useState<Set<string>>(new Set())

  const init = async () => {
    setLoading(true)
    try {
      const [pkgRes, listRes] = await Promise.all([
        getAutomateLoanDocsPackages().catch((e: any) => ({
          __error: e?.response?.data?.message || e?.message || 'Could not load packages from Automate Loan Docs.',
        })),
        getAutomateLoanDocsRecords().catch(() => null),
      ])

      if ((pkgRes as any)?.__error) {
        setPackages([])
        setPackagesError((pkgRes as any).__error)
      } else {
        const pkgList = Array.isArray(pkgRes?.data) ? pkgRes.data : Array.isArray(pkgRes) ? pkgRes : []
        setPackages(pkgList)
        setPackagesError(
          pkgRes?.message || (pkgList.length ? '' : 'No template packages returned by Automate Loan Docs.'),
        )
        const defaultId = pkgRes?.defaultPackageId || pkgList[0]?.id || ''
        setPackageId((prev) => prev || defaultId || '')
      }

      if ((pkgRes as any)?.defaultPackageId) {
        setPackageId((prev) => prev || (pkgRes as any).defaultPackageId)
      }

      setRecords(listRes?.records || [])
      setTableData(listRes?.tableData || {})
      if (listRes?.overrideValues && Object.keys(listRes.overrideValues).length) {
        setOverrideValues(listRes.overrideValues)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    init()
  }, [])

  const onGenerate = async () => {
    if (!packageId.trim()) {
      toast('Enter or select a template package ID first.', { type: 'warning' })
      return
    }
    setLoading(true)
    try {
      const res = await generateAutomateLoanDocs(packageId.trim(), overrideValues)
      if (res?.success && res.data) {
        setRecords((prev) => [res.data, ...prev])
        toast('Automate Loan Docs generated successfully.', { type: 'success' })
      } else if (res?.data) {
        setRecords((prev) => [res.data, ...prev])
        toast(res?.message || 'Generation finished with issues.', { type: 'warning' })
      } else {
        toast(res?.message || 'Failed to generate Automate Loan Docs.', { type: 'error' })
      }
    } catch (e: any) {
      toast(e?.response?.data?.message || e?.message || 'Failed to generate Automate Loan Docs.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onDelete = async (id: number) => {
    const ok = await confirm('Remove this generation record from history?')
    if (!ok) return
    setLoading(true)
    try {
      await deleteAutomateLoanDocsRecord(id)
      setRecords((prev) => prev.filter((r) => r.id !== id))
      toast('Record removed.', { type: 'info' })
    } finally {
      setLoading(false)
    }
  }

  const handleOverrideChange = (keyPath: string, value: string) => {
    setOverrideValues({ ...overrideValues, [keyPath]: value })
  }

  const getOverrideValue = (keyPath: string) => overrideValues[keyPath] || ''

  const clearOverrides = () => {
    setOverrideValues({})
    setEditingItems(new Set())
    toast('Override values cleared', { type: 'info' })
  }

  const toggleEdit = (keyPath: string) => {
    const next = new Set(editingItems)
    if (next.has(keyPath)) next.delete(keyPath)
    else next.add(keyPath)
    setEditingItems(next)
  }

  const isEditing = (keyPath: string) => editingItems.has(keyPath)

  const packageOptions: Record<string, string> = {}
  packages.forEach((p) => {
    const id = String(p.id || '')
    if (id) packageOptions[id] = p.name || p.title || id
  })
  const useManualPackageId = packages.length === 0

  let globalIdx = 1
  const renderTable = () => (
    <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6 mb-2 rounded-md">
      <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200">
        <tr>
          <th scope="col" className="px-3 py-3">
            Category
          </th>
          <th scope="col" className="px-3 py-3 min-w-[200px]">
            Field
          </th>
          <th scope="col" className="px-3 py-3">
            Value
          </th>
          <th scope="col" className="px-3 py-3 min-w-[200px]">
            Override Value
          </th>
          <th scope="col" className="px-3 py-3">
            Status
          </th>
          <th scope="col" className="px-3 py-3">
            Actions
          </th>
        </tr>
      </thead>
      <tbody className="text-[14px] text-gray-900">
        {Object.keys(tableData)
          .sort()
          .map((category, index) => {
            const item = tableData[category]
            const count = Object.keys(item).length
            return (
              <Fragment key={category}>
                {Object.keys(item).map((key, subIndex) => {
                  const fieldData = item[key]
                  const keyPath = fieldData.keyPath || ''
                  const overrideValue = getOverrideValue(keyPath)
                  const hasOverride = overrideValue !== ''
                  return (
                    <tr key={`${category}-${key}`} className={`border ${index % 2 ? 'bg-gray-50' : ''}`}>
                      {!subIndex && (
                        <td className="px-3 py-2" rowSpan={count}>
                          {category}
                        </td>
                      )}
                      <td className="px-3 py-2">
                        {globalIdx++}. {key}
                      </td>
                      <td className="px-3 py-2">{fieldData.value}</td>
                      <td className="px-3 py-2">
                        {isEditing(keyPath) ? (
                          <input
                            type="text"
                            className="w-full px-2 py-1 border border-gray-300 rounded"
                            value={overrideValue}
                            onChange={(e) => handleOverrideChange(keyPath, e.target.value)}
                            autoFocus
                          />
                        ) : (
                          <span className="text-gray-500">{overrideValue || '-'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {hasOverride || fieldData.status ? (
                          <span className="inline-flex text-shade-blue">
                            <CheckIcon className="w-4 h-4" />
                          </span>
                        ) : (
                          <span className="inline-flex text-red-800">
                            <XMarkIcon className="w-4 h-4" />
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className="cursor-pointer hover-shadow1 p-1 text-shade-blue inline-flex"
                          onClick={() => toggleEdit(keyPath)}
                          title={isEditing(keyPath) ? 'Close' : 'Edit'}
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </Fragment>
            )
          })}
      </tbody>
    </table>
  )

  return (
    <div className="AutomateLoanDocs-container relative">
      {hasOverview && <Overview />}
      <LayoutLoading show={loading} />
      <h2 className="text-2xl font-bold mb-4">Automate Loan Docs</h2>
      <p className="text-sm text-gray-600 mb-4">
        Review mapped loan fields below, fix any red items with overrides (state codes must be 2 letters, e.g. AZ), then
        generate the document package.
      </p>

      {!!packagesError && useManualPackageId && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4 max-w-3xl">
          {packagesError} Paste a Package ID from the ALD portal, or set <b>Default Package ID</b> under Admin → Vendors →
          Automate Loan Docs.
        </p>
      )}

      <div className="grid md:grid-cols-2 gap-4 items-end mb-4 max-w-3xl">
        {useManualPackageId ? (
          <RenderInput
            input={{
              inputType: 'text',
              title: 'Template Package ID',
              value: packageId,
              required: true,
              placeholder: 'Paste package ID from Automate Loan Docs',
            }}
            Key="packageId"
            onChange={(_k: string, value: string) => setPackageId(value)}
          />
        ) : (
          <RenderInput
            input={{
              inputType: 'select',
              title: 'Template Package',
              options: packageOptions,
              hasDefaultOption: true,
              value: packageId,
              required: true,
            }}
            Key="packageId"
            onChange={(_k: string, value: string) => setPackageId(value)}
          />
        )}
      </div>

      {!!Object.keys(tableData).length && (
        <div className="mb-4 overflow-auto border rounded">
          {renderTable()}
          <div className="flex justify-between items-center px-3 py-2">
            <div>
              {Object.keys(overrideValues).filter((k) => overrideValues[k]).length > 0 && (
                <Button color="red" onClick={clearOverrides}>
                  {`Clear Overrides (${Object.keys(overrideValues).filter((k) => overrideValues[k]).length})`}
                </Button>
              )}
            </div>
            <Button onClick={onGenerate} disabled={!packageId.trim() || loading}>
              Generate Docs
            </Button>
          </div>
        </div>
      )}

      {!Object.keys(tableData).length && (
        <div className="mb-4">
          <Button onClick={onGenerate} disabled={!packageId.trim() || loading}>
            Generate Docs
          </Button>
        </div>
      )}

      <div className="overflow-auto border rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-2">No</th>
              <th className="p-2">ALD Loan ID</th>
              <th className="p-2">Status</th>
              <th className="p-2">Files</th>
              <th className="p-2">Created</th>
              <th className="p-2">By</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!records.length && (
              <tr>
                <td colSpan={7} className="p-4 text-center text-gray-500">
                  No generations yet.
                </td>
              </tr>
            )}
            {records.map((item, index) => (
              <tr key={item.id || index} className="border-t">
                <td className="p-2">{index + 1}</td>
                <td className="p-2">{item.aldLoanId}</td>
                <td className="p-2">{item.status}</td>
                <td className="p-2">
                  <div className="flex flex-col gap-1">
                    {(item.files || []).map((file: any, i: number) => (
                      <span
                        key={file.fileKey || i}
                        className="text-shade-blue cursor-pointer hover:underline inline-flex items-center gap-1"
                        onClick={() => downloadS3Document(file.fileKey, file.name)}
                      >
                        <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                        {file.name}
                      </span>
                    ))}
                    {!item.files?.length && <span className="text-gray-400">—</span>}
                  </div>
                </td>
                <td className="p-2">{item.createdAt ? formatTime(item.createdAt) : '—'}</td>
                <td className="p-2">{item.email || '—'}</td>
                <td className="p-2">
                  <span
                    className="text-red-700 cursor-pointer hover-shadow1 p-1 rounded inline-flex"
                    onClick={() => onDelete(item.id)}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
